import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number, properties?: Record<string, string>, sellingPlan?: string}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartQty = cart.items.reduce((sum, item) => {
      return item.variant_id.toString() === variantIdInput.value.toString() ? sum + Number(item.quantity || 0) : sum;
    }, 0);

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };


  #extractFormProperties(formData) {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = String(key).match(/^properties\[(.+)\]$/);
      if (!match) continue;
      properties[match[1]] = String(value);
    }
    return properties;
  }

  #isVisiblePreferredLengthProperty(key) {
    const normalized = String(key || '')
      .toLowerCase()
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss')
      .trim();

    return normalized.includes('preferred length') || normalized.includes('wunschlange');
  }

  #isDynamicBiothaneProperty(key) {
    return (
      [
        '_biothane_part',
        '_full_roll_count',
        '_physical_length_m',
        '_remainder_m',
        '_preferred_length_m',
        '_preferred_length_cut_qty',
        '_preferred_length_mode',
      ].includes(key) || this.#isVisiblePreferredLengthProperty(key)
    );
  }

  #getBaseBiothaneProperties(properties) {
    const base = {};
    Object.keys(properties || {}).sort().forEach((key) => {
      if (this.#isDynamicBiothaneProperty(key)) return;
      base[key] = String(properties[key]);
    });
    return base;
  }

  #getPropertySignature(properties) {
    return JSON.stringify(this.#getBaseBiothaneProperties(properties || {}));
  }

  #isBiothaneMeterProperties(properties) {
    return !!(properties && properties._preferred_length_mode === 'meter_quantity');
  }

  #isBiothaneMeterLine(item) {
    return !!(item && item.properties && item.properties._preferred_length_mode === 'meter_quantity');
  }

  #getSellingPlanId(item) {
    return String(
      item?.selling_plan_allocation?.selling_plan?.id ??
      item?.selling_plan_allocation?.selling_plan_id ??
      ''
    );
  }

  #getLogicalBiothaneGroupKey(item) {
    if (!this.#isBiothaneMeterLine(item)) return '';
    return `${Number(item.variant_id || item.id || 0)}::${this.#getSellingPlanId(item)}::${this.#getPropertySignature(item.properties || {})}`;
  }

  #getLogicalCartCount(cart) {
    if (!cart?.items?.length) return 0;

    const seenBiothaneGroups = new Set();
    return cart.items.reduce((total, item) => {
      if (this.#isBiothaneMeterLine(item) && item?.properties?._biothane_part) {
        const groupKey = this.#getLogicalBiothaneGroupKey(item);
        if (groupKey && seenBiothaneGroups.has(groupKey)) return total;
        if (groupKey) seenBiothaneGroups.add(groupKey);
        return total + 1;
      }

      // Count configured cart products/lines, not Shopify's billing quantity.
      return total + 1;
    }, 0);
  }

  #updateLogicalCartCountDisplay(count) {
    const selectors = [
      '[data-cart-count]',
      '[data-cart-count-bubble]',
      '[data-cart-bubble-count]',
      '[data-ref="cartBubbleCount"]',
      '[ref="cartBubbleCount"]',
      '.cart-bubble__count',
      '.cart-count-bubble > span[aria-hidden="true"]',
      '#cart-icon-bubble .cart-count-bubble > span[aria-hidden="true"]',
      '.cart-count',
      '.header__cart-count',
      '.header__icon .count-bubble',
      '.site-header__cart-count',
    ];

    const apply = () => {
      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          node.textContent = String(count);
          node.classList.remove('hidden', 'is-hidden');
          node.removeAttribute('hidden');
        });
      });

      document.querySelectorAll('cart-icon').forEach((icon) => {
        try {
          if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, false, false);
        } catch (_) {}
      });
    };

    apply();
    [80, 220, 500].forEach((delay) => window.setTimeout(apply, delay));
  }

  async #fetchLogicalCartCount() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return 0;
      const cart = await response.json();
      return this.#getLogicalCartCount(cart);
    } catch (error) {
      console.error('Failed to fetch logical cart count:', error);
      return 0;
    }
  }

  #getPreferredLengthDisplayForBillingMeters(billingMeters) {
    const rollBillingMeters = 30;
    const total = Math.max(0, Math.floor(Number(billingMeters || 0)));
    const fullRolls = Math.floor(total / rollBillingMeters);
    const remainderMeters = total - fullRolls * rollBillingMeters;
    const parts = [];

    if (fullRolls > 0) {
      const rollLabel = '30.48m (1 roll)';
      parts.push(rollLabel + (fullRolls > 1 ? ` x ${fullRolls}` : ''));
    }
    if (remainderMeters > 0) parts.push(`${remainderMeters} m`);

    return parts.length ? parts.join(' + ') : `${total} m`;
  }

  #applyPreferredLengthProperties(properties, billingMeters) {
    const next = { ...(properties || {}) };
    next._preferred_length_mode = 'meter_quantity';
    next._preferred_length_m = String(billingMeters);
    next._preferred_length_cut_qty = '1';

    const displayValue = this.#getPreferredLengthDisplayForBillingMeters(billingMeters);
    Object.keys(next).forEach((key) => {
      if (this.#isVisiblePreferredLengthProperty(key)) {
        next[key] = displayValue;
      }
    });

    return next;
  }

  #buildBiothaneSplitItems(variantId, totalBillingQty, baseProperties, sellingPlan) {
    const rollBillingMeters = 30;
    const rollPhysicalMeters = 30.48;
    const total = Math.max(0, Math.floor(Number(totalBillingQty || 0)));
    const fullRollCount = Math.floor(total / rollBillingMeters);
    const fullRollBillingQty = fullRollCount * rollBillingMeters;
    const remainderQty = total - fullRollBillingQty;
    const items = [];

    if (fullRollBillingQty > 0) {
      let properties = { ...(baseProperties || {}) };
      properties._biothane_part = 'full_roll';
      properties._full_roll_count = String(fullRollCount);
      properties._physical_length_m = String(Number((fullRollCount * rollPhysicalMeters).toFixed(2)));
      delete properties._remainder_m;
      properties = this.#applyPreferredLengthProperties(properties, fullRollBillingQty);
      const item = { id: Number(variantId), quantity: fullRollBillingQty, properties };
      if (sellingPlan) item.selling_plan = sellingPlan;
      items.push(item);
    }

    if (remainderQty > 0) {
      let properties = { ...(baseProperties || {}) };
      properties._biothane_part = 'remainder';
      properties._remainder_m = String(remainderQty);
      delete properties._full_roll_count;
      delete properties._physical_length_m;
      properties = this.#applyPreferredLengthProperties(properties, remainderQty);
      const item = { id: Number(variantId), quantity: remainderQty, properties };
      if (sellingPlan) item.selling_plan = sellingPlan;
      items.push(item);
    }

    return items;
  }

  async #addBiothaneMeterQuantity(formData, sectionIds) {
    const variantId = Number(formData.get('id'));
    const addQty = Math.max(1, Math.floor(Number(formData.get('quantity')) || 1));
    const properties = this.#extractFormProperties(formData);
    const baseProperties = this.#getBaseBiothaneProperties(properties);
    const signature = this.#getPropertySignature(baseProperties);
    const sellingPlan = String(formData.get('selling_plan') || '');

    const cartResponse = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error('Failed to fetch cart');
    const cart = await cartResponse.json();

    const matching = (cart.items || []).filter((item) => {
      if (!this.#isBiothaneMeterLine(item)) return false;
      if (Number(item.variant_id) !== variantId) return false;
      if (this.#getSellingPlanId(item) !== sellingPlan) return false;
      return this.#getPropertySignature(item.properties || {}) === signature;
    });

    const existingQty = matching.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const normalizedItems = this.#buildBiothaneSplitItems(variantId, existingQty + addQty, baseProperties, sellingPlan);

    if (matching.length) {
      const updates = {};
      matching.forEach((item) => { updates[item.key] = 0; });
      const removeResponse = await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!removeResponse.ok) throw new Error('Failed to normalize existing Biothane cart lines');
    }

    const addResponse = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: normalizedItems, sections: sectionIds.join(',') }),
    });
    const response = await addResponse.json();
    if (!addResponse.ok || response.status) {
      const err = new Error(response.message || 'Failed to add Biothane item');
      err.response = response;
      throw err;
    }
    return response;
  }

  #captureQueuedItem(variantId, quantity) {
    const form = this.querySelector('form');
    const formData = form ? new FormData(form) : new FormData();
    const properties = this.#extractFormProperties(formData);
    const sellingPlan = String(formData.get('selling_plan') || '');
    return { variantId, quantity, properties, sellingPlan };
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push(this.#captureQueuedItem(intendedVariantId, quantity));
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    formData.set('sections', cartItemComponentsSectionIds.join(','));

    const properties = this.#extractFormProperties(formData);
    if (this.#isBiothaneMeterProperties(properties)) {
      this.#addBiothaneMeterQuantity(formData, cartItemComponentsSectionIds)
        .then(async (response) => {
          const id = formData.get('id');
          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
            this.#setLiveRegionText(addedText);
            setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
          }
          await this.#fetchAndUpdateCartQuantity();
          const logicalCartCount = await this.#fetchLogicalCartCount();
          this.#updateLogicalCartCountDisplay(logicalCartCount);
          this.dispatchEvent(
            new CartAddEvent({}, String(id || this.id), {
              source: 'product-form-component',
              itemCount: logicalCartCount,
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        })
        .catch((error) => {
          console.error(error);
          const response = error?.response || {};
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message || error.message, response.description, response.errors)
          );
          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            this.#setLiveRegionText(response.message || error.message);
          }
        })
        .finally(() => {
          if (event) cartPerformance.measureFromEvent('add:user-action', event);
        });
      return;
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          await this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number, properties?: Record<string, string>, sellingPlan?: string}>} items */
  async #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;
    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const component of cartItemsComponents) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        cartItemComponentsSectionIds.push(component.dataset.sectionId);
      }
    }

    let lastResponse = null;

    try {
      const normalItems = [];

      // IMPORTANT: queued preferred-length Biothane items must use the same
      // normalization path as a normal add-to-cart. This keeps repeated/fast
      // Quick Add actions grouped into 30-unit full rolls plus remainder lines.
      for (const item of items) {
        const properties = item.properties || {};

        if (this.#isBiothaneMeterProperties(properties)) {
          const formData = new FormData();
          formData.set('id', String(item.variantId));
          formData.set('quantity', String(item.quantity));
          if (item.sellingPlan) formData.set('selling_plan', String(item.sellingPlan));

          Object.entries(properties).forEach(([key, value]) => {
            formData.set(`properties[${key}]`, String(value));
          });

          lastResponse = await this.#addBiothaneMeterQuantity(formData, cartItemComponentsSectionIds);
        } else {
          normalItems.push(item);
        }
      }

      // Non-Biothane queued items keep the theme's normal batch-add behavior.
      if (normalItems.length) {
        const payload = {
          items: normalItems.map((item) => {
            const payloadItem = {
              id: Number(item.variantId),
              quantity: item.quantity,
            };
            if (item.properties && Object.keys(item.properties).length) payloadItem.properties = item.properties;
            if (item.sellingPlan) payloadItem.selling_plan = item.sellingPlan;
            return payloadItem;
          }),
          sections: cartItemComponentsSectionIds.join(','),
        };

        const addResponse = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const response = await addResponse.json();
        if (!addResponse.ok || response.status) {
          const error = new Error(response.message || 'Failed to add queued item');
          error.response = response;
          throw error;
        }
        lastResponse = response;
      }

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll('add-to-cart-component')
      );
      const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
      if (anyAddToCartButton) {
        const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
        this.#setLiveRegionText(addedText);
        setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      }

      await this.#fetchAndUpdateCartQuantity();

      const logicalCartCount = await this.#fetchLogicalCartCount();
      this.#updateLogicalCartCountDisplay(logicalCartCount);
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'product-form-component',
          itemCount: logicalCartCount,
          productId: this.dataset.productId,
          sections: lastResponse?.sections,
        })
      );
    } catch (error) {
      console.error(error);
      const response = error?.response || {};

      this.dispatchEvent(
        new CartErrorEvent(
          this.id,
          response.message || error.message,
          response.description,
          response.errors
        )
      );

      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const message = response.message || error.message;
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = message;
        } else {
          addToCartTextError.appendChild(document.createTextNode(message));
        }
        this.#setLiveRegionText(message);
      }
    }
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
